import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';

const AuthDebugInfo: React.FC = () => {
  const { user, profile, loading, profileLoading, error, resetAuthState } = useAuth();
  const [isExpanded, setIsExpanded] = useState(false);
  const [storageInfo, setStorageInfo] = useState<{localStorage: string[], sessionStorage: string[]}>({
    localStorage: [],
    sessionStorage: []
  });

  // Only show in development
  if (import.meta.env.PROD) {
    return null;
  }

  // Scan browser storage for auth-related keys
  const scanStorage = () => {
    const localKeys: string[] = [];
    const sessionKeys: string[] = [];
    
    // Scan localStorage
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (
        key.includes('supabase') || 
        key.includes('auth') || 
        key.includes('session') ||
        key.includes('token') ||
        key.includes('user')
      )) {
        localKeys.push(key);
      }
    }
    
    // Scan sessionStorage
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && (
        key.includes('supabase') || 
        key.includes('auth') || 
        key.includes('session') ||
        key.includes('token') ||
        key.includes('user')
      )) {
        sessionKeys.push(key);
      }
    }
    
    setStorageInfo({ localStorage: localKeys, sessionStorage: sessionKeys });
  };

  const handleForceReset = async () => {
    try {
      await resetAuthState();
      scanStorage(); // Refresh storage info after reset
    } catch (error) {
      console.error('Force reset failed:', error);
    }
  };

  // Scan storage when component expands
  useEffect(() => {
    if (isExpanded) {
      scanStorage();
    }
  }, [isExpanded]);

  const debugInfo = {
    user: user ? {
      id: user.id,
      email: user.email,
      created_at: user.created_at,
      last_sign_in_at: user.last_sign_in_at,
    } : null,
    profile: profile ? {
      id: profile.id,
      username: profile.username,
      email: profile.email,
      role: profile.role,
      has_dev_access: profile.has_dev_access,
    } : null,
    states: {
      loading,
      profileLoading,
      error,
    },
    timestamp: new Date().toISOString(),
  };

  return (
    <div className="fixed bottom-4 left-4 z-50 max-w-sm">
      <div className="bg-gray-900 border border-gray-600 rounded-lg shadow-lg">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full px-3 py-2 text-left text-xs font-mono text-gray-300 hover:text-white transition-colors flex items-center justify-between"
        >
          <span>
          <img 
            src="https://unpkg.com/pixelarticons@1.8.0/svg/wrench.svg" 
            alt="Debug" 
            className="w-4 h-4 inline-block mr-2"
            style={{ filter: 'brightness(0) saturate(100%) invert(1)' }}
          />
          Auth Debug
        </span>
          <span className={`transform transition-transform ${isExpanded ? 'rotate-180' : ''}`}>
            ▼
          </span>
        </button>
        
        {isExpanded && (
          <div className="border-t border-gray-600 p-3 max-h-96 overflow-y-auto">
            <div className="text-xs font-mono text-gray-300 space-y-2">
              <div>
                <div className="text-yellow-400 font-bold">States:</div>
                <div className="ml-2 space-y-1">
                  <div>Loading: <span className={loading ? 'text-red-400' : 'text-green-400'}>{String(loading)}</span></div>
                  <div>Profile Loading: <span className={profileLoading ? 'text-red-400' : 'text-green-400'}>{String(profileLoading)}</span></div>
                  <div>Error: <span className={error ? 'text-red-400' : 'text-green-400'}>{error || 'null'}</span></div>
                </div>
              </div>
              
              <div>
                <div className="text-blue-400 font-bold">User:</div>
                <div className="ml-2">
                  {user ? (
                    <div className="space-y-1">
                      <div>ID: {user.id.slice(0, 8)}...</div>
                      <div>Email: {user.email}</div>
                      <div>Last Sign In: {user.last_sign_in_at ? new Date(user.last_sign_in_at).toLocaleTimeString() : 'Never'}</div>
                    </div>
                  ) : (
                    <span className="text-gray-500">null</span>
                  )}
                </div>
              </div>
              
              <div>
                <div className="text-green-400 font-bold">Profile:</div>
                <div className="ml-2">
                  {profile ? (
                    <div className="space-y-1">
                      <div>Username: {profile.username}</div>
                      <div>Role: {profile.role}</div>
                      <div>Dev Access: <span className={profile.has_dev_access ? 'text-green-400' : 'text-gray-500'}>{String(profile.has_dev_access)}</span></div>
                    </div>
                  ) : (
                    <span className="text-gray-500">null</span>
                  )}
                </div>
              </div>
              
              <div>
                <div className="text-orange-400 font-bold">Browser Storage:</div>
                <div className="ml-2 space-y-1">
                  <div>Local Storage: <span className={storageInfo.localStorage.length > 0 ? 'text-yellow-400' : 'text-green-400'}>
                    {storageInfo.localStorage.length} keys
                  </span></div>
                  {storageInfo.localStorage.length > 0 && (
                    <div className="ml-4 text-xs text-gray-400">
                      {storageInfo.localStorage.slice(0, 3).map(key => (
                        <div key={key}>• {key}</div>
                      ))}
                      {storageInfo.localStorage.length > 3 && <div>• ... and {storageInfo.localStorage.length - 3} more</div>}
                    </div>
                  )}
                  <div>Session Storage: <span className={storageInfo.sessionStorage.length > 0 ? 'text-yellow-400' : 'text-green-400'}>
                    {storageInfo.sessionStorage.length} keys
                  </span></div>
                  {storageInfo.sessionStorage.length > 0 && (
                    <div className="ml-4 text-xs text-gray-400">
                      {storageInfo.sessionStorage.slice(0, 3).map(key => (
                        <div key={key}>• {key}</div>
                      ))}
                      {storageInfo.sessionStorage.length > 3 && <div>• ... and {storageInfo.sessionStorage.length - 3} more</div>}
                    </div>
                  )}
                </div>
              </div>
              
              <div className="pt-2 border-t border-gray-700">
                <div className="text-purple-400 font-bold text-xs">Debug Actions:</div>
                <div className="mt-1 space-y-2">
                  <button
                    onClick={() => navigator.clipboard.writeText(JSON.stringify(debugInfo, null, 2))}
                    className="text-xs bg-gray-800 hover:bg-gray-700 text-gray-300 px-2 py-1 rounded transition-colors mr-2"
                  >
                    Copy JSON
                  </button>
                  <button
                    onClick={handleForceReset}
                    className="text-xs bg-red-800 hover:bg-red-700 text-red-200 px-2 py-1 rounded transition-colors"
                  >
                    🚨 Force Reset Auth
                  </button>
                  <button
                    onClick={scanStorage}
                    className="text-xs bg-blue-800 hover:bg-blue-700 text-blue-200 px-2 py-1 rounded transition-colors ml-2"
                  >
                    <img 
                    src="https://unpkg.com/pixelarticons@1.8.0/svg/search.svg" 
                    alt="Scan" 
                    className="w-4 h-4 inline-block mr-2"
                    style={{ filter: 'brightness(0) saturate(100%) invert(1)' }}
                  />
                  Scan Storage
                  </button>
                </div>
              </div>
              
              <div className="text-xs text-gray-500">
                Updated: {new Date().toLocaleTimeString()}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AuthDebugInfo; 